import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  createEventParticipant,
  updateEventParticipantStatus,
} from 'app/frontend/base/api/event'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { useTeam } from 'app/frontend/base/config/context/team'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { patchTeamEventReferenceCaches } from '../../TeamEvent/utils/teamEventReferenceCache'
import { eventParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

type SignupDefaults = {
  characterId: number
  role: wowaudit.EventParticipantReference['role']
  specId?: number
}

type UpdateEventParticipantStatusInput = {
  eventId: number
  eventParticipantId?: number
  nextStatus: wowaudit.EventParticipantReference['status']
}

type UseEventParticipantStatusMutationOptions = {
  onSuccess?: () => void
  onError?: () => void
}

const useEventParticipantStatusMutation = (
  defaults: SignupDefaults,
  options?: UseEventParticipantStatusMutationOptions,
) => {
  const { team } = useTeam()
  const { accessLevel } = useAuth()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const selectableStatuses = useMemo(
    () =>
      eventParticipantStatuses.filter(
        (nextStatus) =>
          nextStatus !== 'removed' &&
          (accessLevel === 'admin' ||
            team.settings.raidsAvailableStatuses.value.includes(nextStatus)),
      ),
    [accessLevel, team.settings.raidsAvailableStatuses.value],
  )

  const { mutate: updateStatus, isPending: isUpdatingStatus } = useMutation({
    mutationFn: ({
      eventId,
      eventParticipantId,
      nextStatus,
    }: UpdateEventParticipantStatusInput) => {
      if (eventParticipantId) {
        return updateEventParticipantStatus(
          team.id,
          eventParticipantId,
          nextStatus,
        )
      }
      return createEventParticipant(team.id, {
        eventId,
        characterId: defaults.characterId,
        addedAsGuest: true,
        role: defaults.role,
        specId: defaults.specId,
        status: nextStatus,
      })
    },
    onSuccess: (updatedEvent) => {
      patchTeamEventReferenceCaches(queryClient, team.id, updatedEvent)
      toast.success(t('dashboard.team.events.status-updated'))
      options?.onSuccess?.()
    },
    onError: (error) => {
      displayErrorToast(error, t)
      options?.onError?.()
    },
  })

  return {
    selectableStatuses,
    updateStatus,
    isUpdatingStatus,
  }
}

export default useEventParticipantStatusMutation
